import React from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@cfa/react-components';

import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import ScrollModalFooter from '@/components/ScrollModal/ScrollModalFooter';

import { PopupDocument } from '../types';

import { Plan } from '@/_generated/api-clients/xp';

interface AddProcedureFooterProps {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  plan?: Plan;
  isInSection: boolean;
  sectionId: string;
  adminAddingProcedure: boolean;
  onClose: () => void;
  selectedDocuments: PopupDocument[];
  setSelectedDocuments: (documents: PopupDocument[]) => void;
  isPreviewResource: boolean;
  onCustomAddProcedure?: (selectedDocuments: PopupDocument[]) => void;
  textVariant: 'update' | 'default';
}

export const AddProcedureFooter: React.FC<AddProcedureFooterProps> = ({
  scrollRef,
  plan,
  isInSection,
  sectionId,
  adminAddingProcedure,
  onClose,
  selectedDocuments,
  setSelectedDocuments,
  isPreviewResource,
  onCustomAddProcedure,
  textVariant,
}) => {
  const { t } = useTranslation();
  const { addSteps } = useUpdatePlan({
    onSuccess: () => {
      onClose();
      setSelectedDocuments([]);
    },
  });

  const onAddProcedure = async () => {
    if (!plan) return;
    const steps = selectedDocuments.map(doc => ({
      ...doc,
      reference: doc.reference.id,
    }));
    await addSteps(plan, steps, isInSection ? sectionId : undefined);
  };

  const currentCount = selectedDocuments.length;

  const handleCustomAddProcedure = () => {
    onCustomAddProcedure?.(selectedDocuments);
    onClose();
  };

  if (isPreviewResource) return null;

  return (
    <ScrollModalFooter scrollRef={scrollRef}>
      <Button
        color="secondary"
        data-testid="addProcedureButton"
        disabled={currentCount === 0}
        onClick={
          adminAddingProcedure || !!onCustomAddProcedure
            ? handleCustomAddProcedure
            : onAddProcedure
        }
      >
        {textVariant === 'update'
          ? t('Generic.chooseResource')
          : t('Button.addXObjects', {
              count: currentCount,
              object:
                currentCount === 1
                  ? t('Generic.resource')
                  : t('Generic.resources'),
            })}
      </Button>
    </ScrollModalFooter>
  );
};
